import { Button } from "@/components/ui/button";
import { UrlAnswerList } from "@/components/url-answer-list";
import { client } from "@/hera-api/client/client.gen";
import type { QuestionResponseSchema } from "@/hera-api/client/types.gen";
import { isNacStyleAnswerType } from "@/lib/nac-answer-types";
import { normalizePhotoAnswers } from "@/lib/photo-answer";
import { trackedFetch } from "@/lib/server-request-activity";
import {
  formatAnswerValue,
  formatConfirmationAnswer,
  formatNameAddressContactAnswerNameOnly,
  formatPhoneNumber,
  formatQuestionLabelForFeesPayment,
} from "@/lib/utils";
import type { ReactNode } from "react";

type AnswersMap = Record<string, unknown>;

function QaSameLineRow({
  title,
  children,
  valueAlign = "baseline",
}: {
  title: string;
  children: ReactNode;
  valueAlign?: "baseline" | "center";
}) {
  return (
    <div
      className={`flex flex-wrap gap-x-1.5 text-sm ${valueAlign === "center" ? "items-center" : "items-baseline"}`}
    >
      <span className="shrink-0 font-medium text-foreground">
        {title}
        <span className="font-normal text-muted-foreground" aria-hidden>
          :
        </span>
      </span>
      <div className="min-w-0 flex-1 basis-[min(100%,12rem)]">{children}</div>
    </div>
  );
}

function hasAnswer(answer: unknown): boolean {
  if (answer === undefined || answer === null) return false;
  if (typeof answer === "string" && answer.trim() === "") return false;
  if (Array.isArray(answer) && answer.length === 0) return false;
  return true;
}

async function openAnswerDocument(token: string) {
  const baseUrl = client.getConfig().baseUrl || "";
  const auth = client.getConfig().auth;
  const url = `${baseUrl}/shared/answer_files/${token}`;
  try {
    const response = await trackedFetch(url, {
      headers: { Authorization: `Bearer ${auth}` },
    });
    if (!response.ok) {
      throw new Error(`Failed to load document: ${response.statusText}`);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
  } catch (error) {
    console.error("Error loading document:", error);
    window.open(url, "_blank");
  }
}

/**
 * Registration questions flagged for the fees / payment step: show question text
 * and the registrant's answer (or information text for information-only prompts).
 */
export function FeesPaymentScreenQaBlock({
  questions,
  answers,
  className,
}: {
  questions: QuestionResponseSchema[];
  answers: AnswersMap | null | undefined;
  className?: string;
}) {
  const ans = answers ?? {};
  const flagged = questions
    .filter(
      (q) =>
        q.question_type === "registration" && q.show_on_fees_payment_screen,
    )
    .slice()
    .sort((a, b) => a.display_order - b.display_order);

  if (flagged.length === 0) return null;

  const questionHeading = (q: (typeof flagged)[number]) => {
    const short = formatQuestionLabelForFeesPayment(q.question);
    return short || q.question;
  };

  const rows = flagged.flatMap((question) => {
    const raw = ans[question.id];
    const title = questionHeading(question);

    if (question.answer_type === "information") {
      const body =
        question.information_text?.trim() ||
        (hasAnswer(raw) ? formatAnswerValue(raw) : "");
      if (!body) return [];
      return [
        <QaSameLineRow key={question.id} title={title}>
          <p className="text-muted-foreground whitespace-pre-wrap break-words">
            {body}
          </p>
        </QaSameLineRow>,
      ];
    }

    if (question.answer_type === "url") {
      return [
        <QaSameLineRow key={question.id} title={title}>
          <UrlAnswerList value={raw} />
        </QaSameLineRow>,
      ];
    }

    if (question.answer_type === "document") {
      if (typeof raw === "string" && raw) {
        return [
          <QaSameLineRow key={question.id} title={title} valueAlign="center">
            <Button
              variant="link"
              className="h-auto min-h-0 p-0 text-sm text-muted-foreground underline-offset-4"
              onClick={() => openAnswerDocument(raw)}
            >
              View uploaded document
            </Button>
          </QaSameLineRow>,
        ];
      }
      return [
        <QaSameLineRow key={question.id} title={title}>
          <p className="text-muted-foreground whitespace-pre-wrap break-words">
            {formatAnswerValue(raw)}
          </p>
        </QaSameLineRow>,
      ];
    }

    if (question.answer_type === "photo") {
      const photos = normalizePhotoAnswers(raw);
      if (photos.length > 0) {
        return photos.map((photo, index) => (
          <QaSameLineRow
            key={`${question.id}-${index}`}
            title={photos.length > 1 ? `${title} (${index + 1})` : title}
            valueAlign="center"
          >
            <Button
              variant="link"
              className="h-auto min-h-0 p-0 text-sm text-muted-foreground underline-offset-4"
              onClick={() => openAnswerDocument(photo.token)}
            >
              View photo
            </Button>
            <span className="text-muted-foreground text-sm">
              {photo.latitude.toFixed(5)}, {photo.longitude.toFixed(5)} ·{" "}
              {new Date(photo.captured_at).toLocaleString()} ({photo.timezone})
            </span>
          </QaSameLineRow>
        ));
      }
      return [
        <QaSameLineRow key={question.id} title={title}>
          <p className="text-muted-foreground whitespace-pre-wrap break-words">
            {formatAnswerValue(raw)}
          </p>
        </QaSameLineRow>,
      ];
    }

    if (isNacStyleAnswerType(question.answer_type)) {
      const companyFirst =
        question.answer_type === "company_contact" &&
        Boolean(question.use_contact_label);
      return [
        <QaSameLineRow key={question.id} title={title}>
          <p className="text-muted-foreground whitespace-pre-wrap break-words">
            {formatNameAddressContactAnswerNameOnly(raw, { companyFirst })}
          </p>
        </QaSameLineRow>,
      ];
    }

    if (question.answer_type === "confirmation") {
      return [
        <QaSameLineRow key={question.id} title={title}>
          <p className="text-muted-foreground whitespace-pre-wrap break-words">
            {formatConfirmationAnswer(raw)}
          </p>
        </QaSameLineRow>,
      ];
    }

    const formatted =
      question.answer_type === "phone_number" && typeof raw === "string"
        ? formatPhoneNumber(raw)
        : formatAnswerValue(raw);

    return [
      <QaSameLineRow key={question.id} title={title}>
        <p className="text-muted-foreground whitespace-pre-wrap break-words">
          {formatted}
        </p>
      </QaSameLineRow>,
    ];
  });

  if (rows.length === 0) return null;

  return <div className={`space-y-0.5 ${className ?? ""}`}>{rows}</div>;
}
